/**
 * AdminNote — renders one administrator-curated decorative note in the style
 * picked by the admin (post-it / polaroid / letter / kraft tag / speech bubble).
 *
 * Props:
 *  - note: {style, title, body, image_url, color, rotation, author, position}
 *  - className: optional wrapper class for sizing/spacing in a layout
 */
import { Sparkles } from 'lucide-react';

const POSTIT_COLORS = {
  yellow: 'bg-[#FFF7B0] text-stone-900',
  pink:   'bg-[#FFD1DC] text-stone-900',
  green:  'bg-[#C8E6C9] text-stone-900',
  blue:   'bg-[#BBDEFB] text-stone-900',
  cream:  'bg-[#F5EFE0] text-stone-900',
  kraft:  'bg-[#E0CDA9] text-stone-900',
};

function PostIt({ note }) {
  const colorCls = POSTIT_COLORS[note.color] || POSTIT_COLORS.yellow;
  return (
    <div
      className={`${colorCls} px-4 py-3 max-w-[230px] shadow-lg`}
      style={{ transform: `rotate(${note.rotation || 0}deg)`, fontFamily: '"Caveat", "Patrick Hand", cursive' }}
      data-testid="admin-note-postit"
    >
      {note.title && <p className="font-bold text-base leading-tight mb-1">{note.title}</p>}
      {note.body && <p className="text-base leading-snug whitespace-pre-line">{note.body}</p>}
    </div>
  );
}

function Polaroid({ note }) {
  return (
    <div
      className="bg-white p-2 pb-5 shadow-xl border border-stone-200 w-[200px]"
      style={{ transform: `rotate(${note.rotation || 0}deg)` }}
      data-testid="admin-note-polaroid"
    >
      <div className="aspect-square overflow-hidden bg-stone-100 mb-2">
        {note.image_url ? (
          <img src={note.image_url} alt={note.title || 'Polaroid'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-300">
            <Sparkles className="h-8 w-8" />
          </div>
        )}
      </div>
      <p className="text-center text-stone-900 text-sm leading-tight" style={{ fontFamily: '"Caveat", "Patrick Hand", cursive' }}>
        {note.title || note.body}
      </p>
    </div>
  );
}

function Letter({ note }) {
  return (
    <div
      className="bg-[#F8F0DC] px-5 py-4 max-w-[320px] shadow-md border border-amber-900/10 relative"
      style={{
        transform: `rotate(${note.rotation || 0}deg)`,
        backgroundImage: 'repeating-linear-gradient(transparent 0px, transparent 22px, rgba(146, 64, 14, 0.08) 22px, rgba(146, 64, 14, 0.08) 23px)',
        fontFamily: '"Caveat", "Patrick Hand", cursive',
      }}
      data-testid="admin-note-letter"
    >
      {note.title && <p className="text-amber-900 text-xl font-semibold mb-2">{note.title}</p>}
      {note.body && <p className="text-stone-900 text-base leading-relaxed whitespace-pre-line">{note.body}</p>}
      {note.author && (
        <p className="text-right text-amber-900 text-base mt-3 italic">— {note.author}</p>
      )}
    </div>
  );
}

function KraftTag({ note }) {
  return (
    <div className="relative" style={{ transform: `rotate(${note.rotation || 0}deg)` }} data-testid="admin-note-tag">
      {/* "Ficelle" */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-px h-3 bg-stone-500" />
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full border border-stone-500 bg-stone-200" />
      <div className="bg-[#D7B47A] text-stone-900 px-4 py-2 shadow-md rounded-sm" style={{ clipPath: 'polygon(8% 0, 100% 0, 100% 100%, 8% 100%, 0% 50%)' }}>
        {note.title && <p className="font-semibold text-sm leading-tight">{note.title}</p>}
        {note.body && <p className="text-xs leading-snug mt-0.5">{note.body}</p>}
      </div>
    </div>
  );
}

function Bubble({ note }) {
  const speaker = note.author || note.title || 'Ernest';
  const isMarguerite = speaker.toLowerCase().includes('marguerite');
  const bubbleCls = isMarguerite ? 'bg-pink-50 border-pink-300' : 'bg-amber-50 border-amber-300';
  return (
    <div className="max-w-[280px]" style={{ transform: `rotate(${note.rotation || 0}deg)` }} data-testid="admin-note-bubble">
      <div className={`${bubbleCls} border-2 rounded-2xl px-4 py-3 shadow-md relative`}>
        <p className="text-stone-900 text-sm leading-snug whitespace-pre-line">
          <span className="font-semibold mr-1">{speaker} dit :</span>
          {note.body}
        </p>
        {/* Tail */}
        <div className={`absolute -bottom-2 left-6 w-4 h-4 ${bubbleCls} border-l-2 border-b-2 rotate-[-45deg]`} />
      </div>
    </div>
  );
}

const RENDERERS = {
  postit: PostIt,
  polaroid: Polaroid,
  letter: Letter,
  tag: KraftTag,
  bubble: Bubble,
};

export default function AdminNote({ note, className = '' }) {
  if (!note || !note.style) return null;
  const Render = RENDERERS[note.style] || PostIt;
  return (
    <div className={className} data-testid={`admin-note-${note.id || 'preview'}`}>
      <Render note={note} />
    </div>
  );
}

export { POSTIT_COLORS };
